import { toast } from 'react-toastify'
import { addNewLoanCharge, waiveLoanCharge } from 'services'
import { removeComma } from 'utils'

import { LoanChargeType } from '../LoanCharges'
import type { DeferOption } from './LoanPaymentAdjustModal'

/** What the Adjust Payment Credits modal hands back: per category, how much was not applied and where it goes. */
export type DeferredAmounts = Record<string, { amount: number; deferOption: DeferOption; reason?: string }>

/** Amounts the servicer is forgiving rather than carrying, keyed by the payment field they came from. */
export type ChargeWaivers = Record<string, { reason?: string }>

/**
 * The balance each adjustable category is carried in when the payment does not collect it.
 *
 * `lateFee` is deliberately NOT here. A late charge is booked by the server now
 * (`LoanPaymentsService.bookUncollectedLateCharge`), which is the only way a payment taken by
 * phone, drafted by the ACH run, imported or paid in the borrower portal gets one at all. The
 * server books the assessed charge less the fee the payment actually applies, so an amount the
 * Adjust Payment Credits dialog held back is already covered - booking it here as well would
 * charge the borrower for it twice. Forgiving one rides on the payment as
 * `lateChargeWaiveReason` instead.
 */
const deferredChargeTypes: Record<string, LoanChargeType> = {
  interest: LoanChargeType.UnpaidInterest,
}

/**
 * Book a charge the payment raises and, when it is being forgiven rather than carried, waive it on
 * the spot.
 *
 * Waiving rather than simply not booking it is the point: the row stays on the loan as the record
 * that the amount was charged and then given up, with the date, the user and the reason beside it,
 * while its balance goes to zero so nothing bills, collects or quotes it. Same endpoint the Charges
 * screen's waiver uses, so a waiver taken here and one taken there leave the same trail.
 */
const bookCharge = async (payload: Record<string, any>, waive?: { reason?: string }) => {
  const charge: any = await addNewLoanCharge(payload)
  if (!waive) return
  const chargeId = charge?.id
  if (!chargeId) {
    toast('The amount was booked but could not be waived - waive it from the Charges screen.', { type: 'error' })
    return
  }
  try {
    const res: any = await waiveLoanCharge(chargeId, { reason: waive.reason || '' })
    if (!res?.success) toast(res?.error || 'The amount was booked but could not be waived.', { type: 'error' })
  } catch {
    toast('The amount was booked but could not be waived.', { type: 'error' })
  }
}

/**
 * Raise the charges a saved payment implies: whatever the Adjust modal held back, plus the interest
 * the payment did not cover.
 *
 * Shared by the Loan Payments screen and the Apply Payment window (the pipeline's money icon and
 * Online Payments), which post the same payment through the same modal - they used to carry their
 * own copies of this and had already drifted apart.
 */
export const bookPaymentShortfallCharges = async ({
  json,
  loanPaymentId,
  isNewPayment,
  capitalizesInterest,
  deferredValues,
  waivers,
}: {
  /** The payment as saved. */
  json: Record<string, any>
  /** The payment the charges hang off, so deleting it takes them back off the loan. */
  loanPaymentId?: number
  /** An edit re-saves an allocation whose shortfall was already booked; only a new payment raises one. */
  isNewPayment: boolean
  /**
   * A loan on negative amortization takes the shortfall onto its principal balance instead, so it
   * must not also raise a receivable for it - that would charge the borrower twice. The modal
   * already posts 0 in that case; this is the second lock, because the server re-allocates the
   * shortfall from the loan's own setting and the two must not be able to disagree.
   */
  capitalizesInterest: boolean
  deferredValues?: DeferredAmounts
  waivers?: ChargeWaivers
}) => {
  const base = {
    date: json.paymentDueDate,
    interestFromDate: '',
    description: '',
    interestRate: 0,
    accruedInterest: 0,
    loanPaymentId,
  }

  if (deferredValues) {
    for (const key of Object.keys(deferredValues)) {
      const entry = deferredValues[key]
      if (!entry || !deferredChargeTypes[key]) continue
      const amount = entry.amount
      // "Waive" instead of a deferral: booked the same way and then forgiven, so the loan shows the
      // amount was charged and waived rather than silently never charged.
      const waived = entry.deferOption === 'waive'
      await bookCharge(
        {
          ...base,
          type: deferredChargeTypes[key],
          originalBalance: amount,
          unpaidBalance: amount,
          totalDue: amount,
          deferred: !waived,
        },
        waived ? { reason: entry.reason } : undefined,
      )
    }
  }

  // Interest owed but not collected on this payment (the Unpaid Interest field) is recorded as a
  // deferred Unpaid Interest charge so it shows in the servicing Unpaid Interest balance - unless
  // the servicer said on the payment screen that they are forgiving it, in which case the same
  // charge is booked and waived. (The adjust-modal path above handles over-allocation, where Unpaid
  // Interest is 0, so there is no overlap.)
  const unpaidInterest = Number(removeComma(String(json.unpaidInterest ?? ''))) || 0
  if (!isNewPayment || !(unpaidInterest > 0) || capitalizesInterest) return
  const waiveInterest = waivers?.unpaidInterest
  await bookCharge(
    {
      ...base,
      type: LoanChargeType.UnpaidInterest,
      originalBalance: unpaidInterest,
      unpaidBalance: unpaidInterest,
      totalDue: unpaidInterest,
      deferred: !waiveInterest,
    },
    waiveInterest ? { reason: waiveInterest.reason } : undefined,
  )
}
